import { useHouseholdWorkflowTrackersContext } from '@/modules/workflowTrackers/contexts';
import { WorkflowTrackerKind, WorkflowTrackerStatus } from '@/types/schema';

import { useValidationQuestionsAnswers } from '../ValidationQuestionsAnswers.context';

/** The subset of a workflow tracker this module reads. */
interface ValidationQuestionsTracker {
  kind: WorkflowTrackerKind;
  status: WorkflowTrackerStatus;
  householdID?: string | null;
  conversation?: { id: string } | null;
}

/**
 * The ID of the conversation holding `householdId`'s outstanding validation
 * questions, or null when there aren't any.
 *
 * Outstanding means the most recent VALIDATION_QUESTIONS tracker that hasn't
 * reached a terminal status (so neither COMPLETED nor FAILED) and that links to
 * the conversation holding the questions. Trackers arrive newest-first, so the
 * first match is the most recent run. A tracker without a conversation is
 * skipped rather than surfaced: opening that conversation is the only thing the
 * caller can offer the user.
 *
 * The household is matched explicitly rather than assumed: the tracker context
 * lives on a layout route that survives navigation between households, so it
 * still holds the previous household's trackers until the new query resolves.
 */
export function getOutstandingValidationQuestionsConversationId(
  trackers: ValidationQuestionsTracker[],
  householdId: string
): string | null {
  for (const tracker of trackers) {
    if (tracker.householdID !== householdId) continue;
    if (tracker.kind !== WorkflowTrackerKind.ValidationQuestions) continue;
    if (tracker.status !== WorkflowTrackerStatus.Started) continue;

    const conversationId = tracker.conversation?.id;
    if (!conversationId) continue;

    return conversationId;
  }

  return null;
}

interface ValidationQuestionsState {
  outstandingConversationId: string | null;
  answeredAt: Date | null;
  isWaitingForAnswers: boolean;
}

/**
 * The household's current validation-question state.
 *
 * The household subscription reports durable answer completion before the
 * tracker finishes modeling. Terminal trackers remain the fallback for older
 * runs and runs with no questions.
 */
export function useValidationQuestionsState(
  householdId: string
): ValidationQuestionsState {
  const { trackers } = useHouseholdWorkflowTrackersContext();
  const { answeredAt, ready, unavailable } =
    useValidationQuestionsAnswers(householdId);
  const hasAnswered = !!answeredAt;

  const conversationId = getOutstandingValidationQuestionsConversationId(
    trackers,
    householdId
  );
  const isWaitingForAnswers = !!conversationId && !hasAnswered;

  // Avoid a banner flash before the initial response. On error, retain the old
  // tracker behavior, including when the backend field is not deployed yet.
  return {
    outstandingConversationId:
      isWaitingForAnswers && (ready || unavailable) ? conversationId : null,
    answeredAt,
    isWaitingForAnswers,
  };
}
